/**
 * Ticket SK2: Serverfunktionen der Kaufstrecke.
 *
 * `startPurchase` ist seit Ticket SK10 ohne Anmeldung erreichbar und liegt
 * hinter dem Funktionsschalter `checkout_stripe_enabled`, der Bot-Prüfung, der
 * Herkunftsgrenze und der serverseitigen Preisermittlung. In der Adresszeile
 * steht kein Prüfschlüssel.
 */

import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { purchaseFormSchema } from "@/lib/purchases.shared";
import type {
  CheckoutOffer,
  PurchaseAlert,
  PurchaseRow,
  PurchaseStateResult,
  StartPurchaseResult,
} from "@/lib/purchases.server";
import type { StripeReadiness } from "@/lib/stripe-config";

/**
 * Ticket SK14: Absenderadresse für die Herkunftsgrenze. Sie wird NICHT
 * gespeichert — nur ein tageweise gesalzener Kurzwert daraus (siehe
 * `startPurchaseOnServer`).
 */
function clientIp(): string | null {
  try {
    const headers = getRequest().headers;
    const direct = headers.get("cf-connecting-ip");
    if (direct) return direct.trim();
    const forwarded = headers.get("x-forwarded-for");
    const first = forwarded?.split(",")[0]?.trim();
    return first || null;
  } catch {
    return null;
  }
}

function requestOrigin(): string {
  try {
    return new URL(getRequest().url).origin;
  } catch {
    return "https://www.creategy.de";
  }
}

/**
 * Ticket SK10: Angaben zur Kaufseite — öffentlich abrufbar. Die Antwort ist
 * eine feste Feldliste ohne interne Felder und ohne Kennungen.
 */
export const getCheckoutOffer = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z.object({ serviceKey: z.string().trim().min(1).max(100) }).parse(input),
  )
  .handler(async ({ data }): Promise<CheckoutOffer | null> => {
    const { getCheckoutOfferOnServer } = await import("@/lib/purchases.server");
    return getCheckoutOfferOnServer(data.serviceKey);
  });

/**
 * Ticket SK10: ohne Anmeldung erreichbar. Geschützt durch den Funktionsschalter,
 * die Bot-Prüfung, die Adressgrenze und die serverseitige Preisermittlung.
 */
export const startPurchase = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => purchaseFormSchema.parse(input))
  .handler(async ({ data }): Promise<StartPurchaseResult> => {
    const { startPurchaseOnServer } = await import("@/lib/purchases.server");
    return startPurchaseOnServer(data, requestOrigin(), clientIp());
  });

/**
 * Bestätigungsseite. Liest ausschließlich und gibt keine personenbezogenen
 * Angaben zurück.
 */
export const getPurchaseState = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z.object({ sessionId: z.string().trim().min(5).max(200) }).parse(input),
  )
  .handler(async ({ data }): Promise<PurchaseStateResult> => {
    const { getPurchaseStateOnServer } = await import("@/lib/purchases.server");
    return getPurchaseStateOnServer(data.sessionId);
  });

const listSchema = z.object({
  status: z.string().trim().max(30).nullable().optional(),
  mode: z.enum(["test", "live"]).nullable().optional(),
  // Ticket SK11: Sicht des Arbeitsvorrats.
  view: z.enum(["freischaltung", "rechnung", "entscheidung"]).nullable().optional(),
});

export const listPurchases = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => listSchema.parse(input ?? {}))
  .handler(async ({ context, data }): Promise<PurchaseRow[]> => {
    const { assertAdmin } = await import("@/lib/landing-hundert-tage.access.server");
    await assertAdmin(context.supabase, context.userId);
    const { listPurchasesOnServer } = await import("@/lib/purchases.server");
    return listPurchasesOnServer(context.supabase, {
      status: data.status ?? null,
      mode: data.mode ?? null,
      view: data.view ?? null,
    });
  });


export const listPurchaseAlerts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<PurchaseAlert[]> => {
    const { assertAdmin } = await import("@/lib/landing-hundert-tage.access.server");
    await assertAdmin(context.supabase, context.userId);
    const { listPurchaseAlertsOnServer } = await import("@/lib/purchases.server");
    return listPurchaseAlertsOnServer(context.supabase);
  });

/**
 * Ticket SK3: Freischaltung von Hand nachholen. Der Kauf bleibt dabei immer
 * bezahlt; nur der Freischaltungsstand wird von „fehlgeschlagen" zurück auf
 * „offen" gedreht und der Durchlauf erneut angestoßen.
 */
export const retryPurchaseFulfillmentFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ purchaseId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ context, data }): Promise<{ ok: boolean; note: string }> => {
    const { assertAdmin } = await import("@/lib/landing-hundert-tage.access.server");
    await assertAdmin(context.supabase, context.userId);
    const { retryPurchaseFulfillment } = await import("@/lib/purchase-fulfillment.server");
    const result = await retryPurchaseFulfillment(data.purchaseId);
    return { ok: result.ok, note: result.note };
  });

/**
 * Ticket SK4: Rechnung erneut versuchen. Erzeugt keine zweite Rechnung — ist
 * bereits eine Kennung gespeichert oder findet die Rückfrage bei Moco eine
 * Rechnung, wird sie übernommen.
 */
export const retryPurchaseInvoiceFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ purchaseId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ context, data }): Promise<{ ok: boolean; note: string }> => {
    const { assertAdmin } = await import("@/lib/landing-hundert-tage.access.server");
    await assertAdmin(context.supabase, context.userId);
    const { retryPurchaseInvoice } = await import("@/lib/purchase-invoice.server");
    const result = await retryPurchaseInvoice(data.purchaseId);
    return { ok: result.ok, note: result.note };
  });

/**
 * Ticket ER1: Entscheidung „Zugang bleibt bestehen" bei einer Erstattung
 * quittieren. Nur Administratorrolle, nur erstattete Käufe. Status und
 * Freischaltung bleiben unberührt.
 */
export const acknowledgePurchaseDecisionFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        purchaseId: z.string().uuid(),
        note: z.string().trim().max(500).optional(),
      })
      .parse(input),
  )
  .handler(async ({ context, data }): Promise<{ acknowledged: boolean; note: string }> => {
    const { assertAdmin } = await import("@/lib/landing-hundert-tage.access.server");
    await assertAdmin(context.supabase, context.userId);
    const { acknowledgePurchaseDecisionOnServer } = await import("@/lib/purchases.server");
    return acknowledgePurchaseDecisionOnServer(
      context.userId,
      data.purchaseId,
      data.note ?? null,
    );
  });



/** Betriebsart und Bereitschaft der Zugangsdaten — nur Ja/Nein, keine Werte. */
export const getCheckoutReadiness = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<StripeReadiness> => {
    const { assertAdmin } = await import("@/lib/landing-hundert-tage.access.server");
    await assertAdmin(context.supabase, context.userId);
    const { stripeReadiness } = await import("@/lib/stripe.server");
    return stripeReadiness();
  });

/**
 * Ticket SK15: Zielort der früheren Zwischenseite. Öffentlich abrufbar; die
 * Antwort ist ausschließlich ein Pfad der eigenen Website.
 */
export const getOfferPublicPath = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z.object({ serviceKey: z.string().trim().min(1).max(100) }).parse(input),
  )
  .handler(async ({ data }): Promise<{ path: string }> => {
    const { publicOfferPathForServiceKey } = await import("@/lib/purchases.server");
    return { path: await publicOfferPathForServiceKey(data.serviceKey) };
  });
